/**
 * Per-system expected lifespan (years) used to suggest a replacement
 * reminder once the user enters `installed_year`. Conservative defaults
 * — these are the "start watching it" point, not the "must replace" date.
 * Source: industry-standard NAHB/ASHRAE guidance, rounded down.
 */
const LIFESPAN_YEARS: Record<string, number> = {
  // HVAC
  furnace: 18,
  boiler: 25,
  heatpump: 15,
  mini: 15,
  ac: 15,
  baseboard: 30,
  radiant: 30,

  // Water
  water_heater: 10,
  wh: 10,
  tankless_wh: 18,
  softener: 12,
  filtration: 8,

  // Major appliances
  fridge: 14,
  range: 14,
  oven: 14,
  microwave: 9,
  hood: 12,
  dw: 10,
  dishwasher: 10,
  washer: 11,
  dryer: 13,
  disposal: 10,
  disp: 10,

  // Comfort / safety
  humidifier: 8,
  dehumidifier: 8,
  humid: 8,
  air_purifier: 7,
  smoke_co: 10,

  // Outside
  roof_asphalt: 25,
  roof_metal: 50,
  pool: 20,
  hottub: 15,
  sump: 10,
  septic: 30,
  solar: 25,
  garage_door_opener: 15,
};

/**
 * Returns a suggested reminder for a system based on its kind +
 * installed_year, or null if we don't have enough info or the suggestion
 * would be in the past / too soon to matter.
 */
export function suggestReplacementReminder(
  kind: string | null | undefined,
  installedYear: number | null | undefined,
  label?: string
): { at: number; label: string } | null {
  if (!kind || !installedYear) return null;
  const lifespan = LIFESPAN_YEARS[kind];
  if (!lifespan) return null;
  // Aim at the start of the lifespan window — November so it doesn't
  // collide with summer; gives the user months of heads-up before deep
  // winter dependence.
  const at = new Date(installedYear + lifespan, 10, 1, 9, 0, 0).getTime();
  // Don't suggest dates already in the past or less than 30 days out —
  // those aren't reminders, they're alerts the user has already missed.
  if (at - Date.now() < 30 * 24 * 60 * 60 * 1000) return null;
  return {
    at,
    label: label ?? `${prettyKind(kind)} — start watching for replacement`,
  };
}

function prettyKind(kind: string): string {
  return kind
    .replace(/_/g, ' ')
    .replace(/\b(wh|ac|dw|disp|humid)\b/i, (m) => {
      const map: Record<string, string> = {
        wh: 'water heater',
        ac: 'AC',
        dw: 'dishwasher',
        disp: 'disposal',
        humid: 'humidifier',
      };
      return map[m.toLowerCase()] ?? m;
    })
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
